import { Button } from '@mui/material';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { makeStyles } from 'tss-react/mui';

import { APP_SETUP_PAGE } from 'src/constants';
import { RouteContext, useFeatureFlags } from 'src/context';
import { AppSettingsModalContext } from 'src/features/app-settings-modal/AppSettingsModalContext';
import history from 'src/history';
import { resolveRootModuleIds } from 'src/legacy/components/Settings/AppSetupPage/SetupApp/resolveRootModuleId';
import { useEditingApp } from 'src/legacy/components/Settings/AppSetupPage/SetupApp/useEditingApp';
import BaseTypography from 'src/legacy/components/Text/BaseTypography';
import { useGetInstallsQuery } from 'src/services/api/applicationsApi';
import { GraySmall, NonHoverBorder, red } from 'src/theme/colors';

import { useRemoveAppAction } from './useRemoveAppAction';

const useStyles = makeStyles()((theme) => ({
  deleteButton: {
    marginTop: theme.spacing(3),
    backgroundColor: red,
    borderColor: red,
    '&:hover': {
      backgroundColor: red,
    },
  },
  disabledButton: {
    marginTop: theme.spacing(3),
    backgroundColor: 'transparent',
    borderColor: NonHoverBorder,
    color: red,
    '&:hover': {
      backgroundColor: 'transparent',
    },
  },
  removeAppContent: {
    color: GraySmall,
    paddingTop: theme.spacing(0.5),
  },
}));

interface removeAppQuery {
  moduleId?: string;
  appId?: string;
}

/**
 * @description - Responsible for rendering the remove app button and the modal.
 * For core apps (Forms, Helpdesk, Billing etc), we only disable the app.
 * For other apps, we delete the app.
 * Additionally, we do not render this component when we're in add mode.
 */
export const RemoveApp = () => {
  const { query } = React.useContext(RouteContext);
  const modalCtx = React.useContext(AppSettingsModalContext);
  const { EnableConsistentAppSettingsLink } = useFeatureFlags();
  const appModule = useEditingApp();
  const { t } = useTranslation();
  const isAddMode = (query as removeAppQuery).moduleId === 'new';
  const { data: { moduleSettings = [], extensionsSettings } = {} } =
    useGetInstallsQuery('withFolders');
  const { classes } = useStyles();
  const moduleConfig =
    'moduleId' in query &&
    moduleSettings.find(
      (module) => module.id === (query as removeAppQuery).moduleId,
    );
  const editingModuleId =
    'moduleId' in query ? (query as removeAppQuery).moduleId : undefined;
  const editingExtensionConfig = editingModuleId
    ? extensionsSettings?.[editingModuleId]
    : undefined;
  const appId: string =
    editingExtensionConfig?.appId ??
    ('appId' in query ? ((query as removeAppQuery).appId as string) : '');
  const isMarketplaceApp = !!(
    moduleConfig && extensionsSettings?.[moduleConfig.id]?.appId
  );

  // Multi-instance siblings — entries that share the same AppID. The root
  // of a group can only be deleted once it is the last remaining variant;
  // non-root variants can always be deleted.
  const rootIDs = resolveRootModuleIds(extensionsSettings);
  const siblingExtensionIds =
    appId && editingModuleId
      ? Object.entries(extensionsSettings || {})
          .filter(
            ([extId, ext]) => ext?.appId === appId && extId !== editingModuleId,
          )
          .map(([extId]) => extId)
      : [];
  const siblingCount = siblingExtensionIds.length;
  const isRootVariant =
    !!editingModuleId && rootIDs[editingModuleId] === editingModuleId;
  const blockRootDeletion = isRootVariant && siblingCount > 0;

  const { openRemoveAppModal, modal, isCoreApp } = useRemoveAppAction({
    appModule,
    appId,
    onComplete: () => {
      if (modalCtx && EnableConsistentAppSettingsLink) {
        modalCtx.onClose();
      } else {
        history.push(APP_SETUP_PAGE.path);
      }
    },
  });

  if (isAddMode) return null;

  return (
    <div>
      {modal}
      {!appModule?.disabled && (
        <div>
          <BaseTypography fontType="18Medium">
            {t(($) =>
              isCoreApp
                ? $.page_apps.remove_app_modal.disable.title
                : $.page_apps.remove_app_modal.remove.title,
            )}
          </BaseTypography>
          {!isMarketplaceApp && (
            <BaseTypography
              fontType="13Regular"
              className={classes.removeAppContent}
            >
              {t(($) =>
                isCoreApp
                  ? $.page_apps.remove_app_modal.disable.description
                  : $.page_apps.remove_app_modal.remove.description,
              )}
            </BaseTypography>
          )}
          {blockRootDeletion && (
            <BaseTypography
              fontType="13Regular"
              className={classes.removeAppContent}
            >
              This is the original instance of this app. Remove the other{' '}
              {siblingCount > 1 ? `${siblingCount} instances` : 'instance'}{' '}
              before removing this one.
            </BaseTypography>
          )}
          <Button
            onClick={openRemoveAppModal}
            variant="contained"
            color="primary"
            disabled={blockRootDeletion}
            className={
              isCoreApp || blockRootDeletion
                ? classes.disabledButton
                : classes.deleteButton
            }
          >
            {t(($) => (isCoreApp ? $.common.disable : $.common.delete))}
          </Button>
        </div>
      )}
    </div>
  );
};
